import { useTranslation } from 'react-i18next';
import type { DirectSalePriceQuote } from '@designdeck/shared';
import type { Product } from '@/api/products';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DirectSaleMockupPreview } from '../DirectSaleMockupPreview';
import { formatMoney } from '../format-money';

/**
 * How the price comes about: the product, what each printed area adds and,
 * once pieces are chosen, what each size costs. Every amount is the quote's
 * own — the dialog adds nothing up, so it cannot disagree with what the
 * buying system charges.
 */
export function DirectSalePriceDetailsDialog({
  open,
  onOpenChange,
  product,
  quote,
  pieces,
  previewSlug,
  previewDataUrl,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
  quote: DirectSalePriceQuote;
  /** Pieces over all sizes; zero means the quote prices a single piece. */
  pieces: number;
  /** The view shown next to the product name; none without a mockup. */
  previewSlug: string | null;
  previewDataUrl: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const money = (amount: string | number) =>
    formatMoney(amount, quote.currency, i18n.language);

  const heading = 'text-[13px] font-bold uppercase tracking-wide text-ink';
  const row = 'flex items-baseline justify-between gap-4 py-1.5';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('directSale.design.close')}
        aria-describedby={undefined}
        className="max-h-[calc(100vh-4rem)] overflow-y-auto"
      >
        <DialogTitle>{t('directSale.checkout.priceDetails')}</DialogTitle>

        <section className="flex flex-col gap-2">
          <h3 className={heading}>{t('directSale.desktop.yourProduct')}</h3>
          <div className="flex items-center gap-3">
            {previewSlug && (
              <DirectSaleMockupPreview
                product={product}
                positionSlug={previewSlug}
                designDataUrl={previewDataUrl}
                fit={1}
                className="h-16 w-14 flex-none rounded-control bg-[rgb(var(--color-canvas-bg))]"
              />
            )}
            <span className="flex-1 text-sm text-ink">{product.name}</span>
            <span className="text-sm font-semibold text-ink">
              {money(quote.basePrice)}
            </span>
          </div>
        </section>

        {quote.positions.length > 0 && (
          <section className="flex flex-col border-t border-line pt-4">
            <h3 className={heading}>{t('directSale.desktop.printCosts')}</h3>
            <p className="pb-1 text-[13px] text-mute">
              {t('directSale.desktop.printCostsHint')}
            </p>
            {quote.positions.map((line) => (
              <div key={line.positionId} className={row}>
                <span className="text-sm text-ink-2">
                  {t('directSale.checkout.printArea', {
                    position: line.positionName,
                  })}
                </span>
                <span className="text-sm font-semibold text-ink">
                  {money(line.amount)}
                </span>
              </div>
            ))}
          </section>
        )}

        {/* Without a chosen size the quote's single item is a stand-in for
            "one piece", not something the customer picked. */}
        {pieces > 0 && (
          <section className="flex flex-col border-t border-line pt-4">
            <h3 className={heading}>{t('directSale.desktop.pieces')}</h3>
            {quote.items.map((item) => (
              <div key={item.sizeId ?? 'default'} className={row}>
                <span className="text-sm text-ink-2">
                  {t('directSale.desktop.sizeSummaryItem', {
                    count: item.quantity,
                    size: item.sizeName ?? '',
                  })}
                </span>
                <span className="text-sm font-semibold text-ink">
                  {money(item.amount)}
                </span>
              </div>
            ))}
          </section>
        )}

        <footer className="flex flex-col border-t border-line pt-4">
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-sm font-bold text-ink">
              {pieces > 0
                ? t('directSale.checkout.total')
                : t('directSale.desktop.unitPriceNote')}
            </span>
            <span className="font-display text-[22px] font-bold text-ink">
              {money(quote.total)}
            </span>
          </div>
          <p className="text-right text-xs text-mute">
            {t('directSale.checkout.vatNote', { rate: quote.vatRate })}
          </p>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
